<script lang="ts">
import PageContainer from '@/components/PageContainer.vue'
import ProjectVideoPlayer from '@/components/ProjectVideoPlayer.vue'
import ImageGallery from '@/components/ImageGallery.vue'
import { api } from '@/api/api.js'
import { updateMeta, addJsonLd, getVideoSchema, getCanonical } from '@/utils/seo.js'
import type { Project } from '@/types/api'

export default {
  data() {
    return {
      project: null as Project | null,
      projectId: null as string | number | null,
      isLoading: true as boolean,
      error: null as string | null,
    }
  },
  components: {
    PageContainer,
    ProjectVideoPlayer,
    ImageGallery,
  },
  watch: {
    project(p: Project | null) {
      if (!p) return
      // Динамические meta и VideoObject (VEO) после загрузки проекта
      updateMeta({
        title: p.title,
        description: (p.content || 'Проект медиа-продакшена Chapterfour.').slice(0, 160),
        canonicalPath: this.$route.path,
        ogImage: p.cover_url || undefined,
      })
      if (p.main_video_url) {
        addJsonLd(
          getVideoSchema({
            name: p.title,
            description: (p.content || '').slice(0, 200),
            thumbnailUrl: p.cover_url,
            contentUrl: p.main_video_url.endsWith?.('.mp4') ? p.main_video_url : undefined,
            embedUrl: getCanonical(this.$route.path),
          }),
          'seo-json-ld-extra',
        )
      }
    },
  },
  computed: {
    mainVideoSources(): { src: string; type: string }[] {
      if (!this.project?.main_video_url) return []
      return [
        {
          src: this.project.main_video_url,
          type: this.getVideoType(this.project.main_video_url),
        },
      ]
    },
    backstageVideoSources(): { src: string; type: string }[] {
      if (!this.project?.backstage_video_url) return []
      return [
        {
          src: this.project.backstage_video_url,
          type: this.getVideoType(this.project.backstage_video_url),
        },
      ]
    },
    projectGalleryImages(): { url: string }[] {
      // Галерея изображений проекта
      const images = this.project?.backstage_images || []

      // Если это массив строк, преобразуем в массив объектов
      if (Array.isArray(images) && images.length > 0) {
        return images.map((img) => {
          if (typeof img === 'string') {
            return { url: img }
          }
          return img
        })
      }

      return []
    },
    hasGalleryImages(): boolean {
      return this.projectGalleryImages.length > 0
    },
  },
  methods: {
    getVideoType(url: string | undefined): string {
      if (!url) return 'video/mp4'
      if (url.endsWith('.m3u8')) return 'application/x-mpegURL'
      if (url.endsWith('.webm')) return 'video/webm'
      if (url.endsWith('.ogg')) return 'video/ogg'
      return 'video/mp4'
    },
  },
  async mounted() {
    this.projectId = (this as any).$route.params.id
    this.isLoading = true
    this.error = null
    try {
      this.project = await api.getProject(this.projectId as string | number)
    } catch (err) {
      console.error('Ошибка при получении проекта:', err)
      this.error = 'Не удалось загрузить проект. Попробуйте позже.'
    } finally {
      this.isLoading = false
    }
  },
}
</script>

<template>
  <PageContainer>
    <div v-if="isLoading" class="project-loader">
      <div class="project-loader-header"></div>
      <div class="project-loader-subtitle"></div>
      <div class="project-loader-video"></div>
    </div>
    <div v-else-if="error" class="project-error">
      <p>{{ error }}</p>
    </div>
    <div v-else class="project-container">
      <header class="project-header">
        <h1 class="text-xxl">{{ project?.title }}</h1>
        <p class="text-lg">{{ project?.category || 'Без категории' }}</p>
      </header>
      <div class="project-content">
        <div class="project-video" v-if="project?.main_video_url && mainVideoSources.length > 0">
          <ProjectVideoPlayer
            :video-sources="mainVideoSources"
            :title="project?.title || 'Video'"
            :cover-image="project?.cover_url"
            loader="Загрузка видео..."
          />
        </div>
        <!-- <div class="project-video" v-if="project?.backstage_video_url && backstageVideoSources.length > 0">
          <h1>BACKSTAGE</h1>
          <ProjectVideoPlayer
            :video-sources="backstageVideoSources"
            title="BACKSTAGE"
            loader="Загрузка видео..."
          />
        </div> -->
        <div class="project-info">
          <div class="project-description" v-if="project?.content">
            <p class="text-md description-text">{{ project.content }}</p>
          </div>

          <div class="project-users" v-if="project?.users && project.users.length > 0">
            <h2 class="text-lg section-title">Участники проекта</h2>
            <div class="users-list">
              <div v-for="user in project.users" :key="user.user.username" class="user-item">
                <router-link :to="'/author/' + user.user.username" class="user-name">
                  {{ user.user.title }}
                </router-link>
                <span class="user-separator">—</span>
                <span class="user-role">{{ user.role }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-if="hasGalleryImages" class="project-gallery">
          <ImageGallery :images="projectGalleryImages" />
        </div>
      </div>
    </div>
  </PageContainer>
</template>

<style scoped>
.project-loader {
  margin: 2.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.project-loader-header,
.project-loader-subtitle {
  height: 1.5rem;
  border-radius: 0;
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  animation: project-skeleton-shimmer 1.4s ease-in-out infinite;
}

.project-loader-header {
  width: 50%;
}

.project-loader-subtitle {
  width: 30%;
}

.project-loader-video {
  margin-top: 1.5rem;
  width: 100%;
  height: 18rem;
  border-radius: 0;
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  animation: project-skeleton-shimmer 1.4s ease-in-out infinite;
}

.project-error {
  margin: 2.5rem 0;
  text-align: center;
  color: #b91c1c;
}

@keyframes project-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.project-container {
  margin: 2.5rem 0;
}

.project-header {
  margin-bottom: 2.5rem;
}

.project-content {
  width: 100%;
}

.project-video {
  margin-bottom: 2.5rem;
}

.project-gallery {
  margin: 2rem 0;
}

.project-info {
  margin-top: 3rem;
}

.section-title {
  margin-bottom: 1.5rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.project-description {
  margin-bottom: 3rem;
}

.description-text {
  line-height: 1.8;
  color: #000;
  max-width: 100%;
}

.project-users {
  margin-top: 2rem;
}

.users-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.user-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
}

.user-item:last-child {
  border-bottom: none;
}

.user-item:hover {
  padding-left: 0.5rem;
}

.user-name {
  font-size: 1rem;
  font-weight: 600;
  color: #000;
  text-decoration: none;
  transition: color 0.3s ease;
  white-space: nowrap;
  text-transform: uppercase;
}

.user-name:hover {
  color: rgba(0, 0, 0, 0.7);
}

.user-separator {
  color: rgba(0, 0, 0, 0.3);
  font-weight: 300;
  flex-shrink: 0;
}

.user-role {
  font-size: 0.9375rem;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 400;
  text-transform: uppercase;
}

@media (max-width: 64rem) {
  .project-header,
  .project-video {
    margin-bottom: 1.875rem;
  }

  .project-gallery {
    margin: 2rem 0 1.5rem;
  }

  .project-info {
    margin-top: 2rem;
  }

  .section-title {
    margin-bottom: 1rem;
  }

  .project-description {
    margin-bottom: 2rem;
  }

  .user-name {
    font-size: 0.9375rem;
  }

  .user-role {
    font-size: 0.875rem;
  }
}

@media (max-width: 48rem) {
  .user-item {
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
  }

  .user-separator {
    display: none;
  }

  .user-name {
    flex: 1;
    min-width: 0;
    white-space: normal;
  }

  .user-role {
    flex-shrink: 0;
    text-align: right;
    max-width: 48%;
  }
}
</style>
